import React from "react";
import SearchField from "../../common/SearchField";
import GenericDropDownList from "../../common/GenericDropDownList";
import AdvTaskStatusMultiSelect, {
  SelectedStatus,
} from "./AdvTaskStatusMultiSelect";
import { ClientTaskType } from "../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { useTranslation } from "react-i18next";

interface AdvTaskListingSearchAndFilterProps {
  onSearch: (searchValue: string | null) => void;
  isLoading: boolean;
  loaderColor: string;
  selectedStatuses: SelectedStatus[];
  setSelectedStatuses: (statuses: SelectedStatus[]) => void;
  searchTerm: string | null;
  taskType?: ClientTaskType | null;
  selectedCategory: string;
  setSelectedCategory: (categoryId: string) => void;
}

const AdvTaskListingSearchAndFilter: React.FC<
  AdvTaskListingSearchAndFilterProps
> = ({
  onSearch,
  isLoading,
  loaderColor,
  selectedStatuses,
  setSelectedStatuses,
  searchTerm,
  taskType,
  selectedCategory,
  setSelectedCategory,
}) => {
  const { t } = useTranslation();

  // Category options for dropdown
  const hasCategoryOptions =
    taskType?.categories && taskType.categories.length > 0;
  const categoryOptions = hasCategoryOptions
    ? [
        { key: "All Categories", value: "All Categories" },
        ...taskType!.categories!.map((category) => ({
          key: category.categoryId,
          value: t(category.nameTranslationKeyIdentifier),
        })),
      ]
    : [];

  // Determine the current value for the category dropdown
  const categoryDropdownValue = selectedCategory;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border border-gray-200 rounded-lg mb-4 gap-3">
      <SearchField
        onSearch={onSearch}
        isLoading={isLoading}
        loaderColor={loaderColor}
        searchTerm={searchTerm}
        className="flex-grow w-full lg:max-w-md sm:mr-4"
        includeSetWidth={false}
        inputFieldClassName="text-sm focus:outline-none focus:ring-0 focus:border-gray-300 bg-white border-gray-200"
        placeholderText="Search by name for a task or team member..."
        outerContainerClassName="w-full"
      />
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
        {hasCategoryOptions && (
          <div className="w-full sm:w-auto">
            <GenericDropDownList
              inputId="category-filter"
              items={categoryOptions}
              currentValue={categoryDropdownValue}
              onChange={(value) => {
                setSelectedCategory(value || "All Categories");
              }}
              className="w-full sm:w-48 px-2.5 py-1.5 text-sm border-gray-200"
              applyBorder={true}
              bgColorClassName="focus:outline-none focus:ring-0 focus:border-gray-300"
              includeSelectOption={false}
            />
          </div>
        )}

        <div className="w-full sm:w-auto">
          <AdvTaskStatusMultiSelect
            selectedStatuses={selectedStatuses}
            onSelectionChange={setSelectedStatuses}
            className="w-full sm:w-48"
          />
        </div>
      </div>
    </div>
  );
};

export default AdvTaskListingSearchAndFilter;
